import type { ProjectionMonth, FinanceSummary } from "../../lib/types";

interface Props {
  summary: FinanceSummary;
  snapshots: ProjectionMonth[];
}

const fmt = (n: number) =>
  n.toLocaleString("es-MX", { maximumFractionDigits: 0 });

export function LiquidationProgress({ summary, snapshots }: Props) {
  const first = snapshots[0];
  const initialDebt = first ? first.total_debt : summary.total_debt;
  const currentDebt = summary.total_debt;
  const paid = initialDebt - currentDebt;
  const paidPercent = initialDebt > 0 ? (paid / initialDebt) * 100 : 0;

  // Find when debt reaches 0
  const zeroMonth = snapshots.find((s) => s.total_debt <= 0);
  const monthsToFree = zeroMonth ? zeroMonth.month : snapshots.length;

  // Chart dimensions
  const chartW = 100;
  const chartH = 40;
  const maxDebt = Math.max(...snapshots.map((s) => s.total_debt), 1);

  // Show max ~12 labels
  const step = Math.max(1, Math.floor(snapshots.length / 12));

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-border">
        <h3 className="text-sm font-medium text-gray-300">
          Progreso de Liquidacion
        </h3>
      </div>
      <div className="p-4 space-y-4">
        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500">
              ${fmt(currentDebt)} restante de ${fmt(initialDebt)}
            </span>
            <span className="text-xs text-accent font-medium">
              {paidPercent.toFixed(1)}% pagado
            </span>
          </div>
          <div className="w-full h-3 rounded-full bg-surface-2 overflow-hidden">
            <div
              className="h-full rounded-full bg-accent transition-all"
              style={{ width: `${Math.min(paidPercent, 100)}%` }}
            />
          </div>
        </div>

        {/* Strategy */}
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span>
            Estrategia: <span className="text-gray-300">Avalancha</span> (mayor
            tasa primero)
          </span>
          <span>
            Libre en:{" "}
            <span className="text-accent font-medium">~{monthsToFree} meses</span>
          </span>
        </div>

        {/* Order */}
        <div className="flex items-center gap-2 flex-wrap">
          {first &&
            first.cards
              .filter((c) => c.balance > 0)
              .map((c, i, arr) => (
                <div key={c.id} className="flex items-center gap-1">
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: c.color }}
                  />
                  <span className="text-xs text-gray-400">{c.name}</span>
                  {i < arr.length - 1 && (
                    <span className="text-gray-600 mx-1">&rarr;</span>
                  )}
                </div>
              ))}
        </div>

        {/* SVG Chart */}
        {snapshots.length > 1 && (
          <div className="mt-2">
            <svg
              viewBox={`0 0 ${chartW} ${chartH + 8}`}
              className="w-full h-32"
              preserveAspectRatio="none"
            >
              {/* Grid lines */}
              {[0, 0.25, 0.5, 0.75, 1].map((p) => (
                <line
                  key={p}
                  x1={0}
                  y1={chartH * p}
                  x2={chartW}
                  y2={chartH * p}
                  stroke="#30363D"
                  strokeWidth="0.2"
                />
              ))}

              {/* Area fill */}
              <path
                d={
                  `M 0 ${chartH} ` +
                  snapshots
                    .map((s, i) => {
                      const x = (i / (snapshots.length - 1)) * chartW;
                      const y = chartH - (s.total_debt / maxDebt) * chartH;
                      return `L ${x} ${y}`;
                    })
                    .join(" ") +
                  ` L ${chartW} ${chartH} Z`
                }
                fill="url(#debtGradient)"
              />

              {/* Line */}
              <polyline
                points={snapshots
                  .map((s, i) => {
                    const x = (i / (snapshots.length - 1)) * chartW;
                    const y = chartH - (s.total_debt / maxDebt) * chartH;
                    return `${x},${y}`;
                  })
                  .join(" ")}
                fill="none"
                stroke="#00BCD4"
                strokeWidth="0.5"
              />

              {/* Labels */}
              {snapshots.map(
                (s, i) =>
                  i % step === 0 && (
                    <text
                      key={i}
                      x={(i / (snapshots.length - 1)) * chartW}
                      y={chartH + 6}
                      textAnchor="middle"
                      className="fill-gray-600"
                      fontSize="2.5"
                    >
                      {s.label}
                    </text>
                  )
              )}

              <defs>
                <linearGradient
                  id="debtGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#00BCD4" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#00BCD4" stopOpacity="0.02" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}
